import { useState, useEffect } from "react";
import { FacetCard } from "./components/FacetCard";
import { Products } from "./components/Products";
import { CategoryText } from "./components/CategoryText";
import { PageTitle } from "./components/PageTitle";
import { Banner } from "./components/Banner";
import { FilterTags } from "./components/FilterTags";
import { parseQueryParams, updateQueryParams } from "./utils/queryParams";
import { eventBus, EVENTS } from "./utils/eventBus";
import "./App.css";
import "./styles.css";

interface AppProps {
  groupId: string;
  groupName: string;
  cartId: string;
}

function App({ groupId, groupName, cartId }: AppProps) {
  const [productFlags, setProductFlags] = useState<Record<string, string[]>>(
    {}
  );
  const [productBadges, setProductBadges] = useState<Record<string, string[]>>(
    {}
  );
  const [couponDiscountAmounts, setCouponDiscountAmounts] = useState<
    Record<string, number>
  >({});
  // Handle filter toggling (add or remove)
  const handleFilterToggle = (data: {
    facet: string;
    value: string;
    checked: boolean;
  }) => {
    const params = parseQueryParams();

    if (data.checked) {
      // Add filter
      params.cp.push(`${data.facet}:${data.value}`);
    } else {
      // Remove filter
      params.cp = params.cp.filter((filter) => {
        const [filterFacet, filterValue] = filter.split(":");
        return !(filterFacet === data.facet && filterValue === data.value);
      });
    }

    // Reset page to 1 when a filter is changed
    params.page = "1";

    // Update URL
    updateQueryParams(params);

    // Emit event to notify all components
    eventBus.emit(EVENTS.FILTERS_CHANGED, params);
  };

  // Listen for events
  useEffect(() => {
    const handleProductDataUpdate = (productData: any) => {
      if (productData?.flags) {
        setProductFlags(productData.flags);
      }
      if (productData?.badges) {
        setProductBadges(productData.badges);
      }
      if (productData?.couponDiscountAmounts) {
        setCouponDiscountAmounts(productData.couponDiscountAmounts);
      }
    };

    // Listen for filter removal
    const handleFilterRemoved = (data: { facet: string; value: string }) => {
      handleFilterToggle({ ...data, checked: false });
    };

    // Listen for filter toggling
    const handleFilterToggledEvent = (data: {
      facet: string;
      value: string;
      checked: boolean;
    }) => {
      handleFilterToggle(data);
    };

    // Listen for product data updates
    eventBus.on("product_data_updated", handleProductDataUpdate);
    eventBus.on(EVENTS.FILTER_REMOVED, handleFilterRemoved);
    eventBus.on(EVENTS.FILTER_TOGGLED, handleFilterToggledEvent);

    return () => {
      eventBus.off("product_data_updated", handleProductDataUpdate);
      eventBus.off(EVENTS.FILTER_REMOVED, handleFilterRemoved);
      eventBus.off(EVENTS.FILTER_TOGGLED, handleFilterToggledEvent);
    };
  }, []);

  return (
    <div className="">
      <div className="flex flex-col lg:flex-row gap-8 view-category-page">
        {/* Facets Column */}
        <div className="sidebar_first sidebar col-md-4 order-first">
          {/* Active Filters */}
          <div className="active-filters mb-4">
            <FilterTags groupId={groupId} />
          </div>
          <FacetCard groupId={groupId} />
        </div>

        {/* Main Content Column */}
        <div className="listing-content">
          {/* Page Title */}
          <PageTitle groupName={groupName} />

          {/* Banner */}
          <Banner groupId={groupId} />

          {/* Products */}
          <Products
            groupId={groupId}
            cartId={cartId}
            productFlags={productFlags}
            productBadges={productBadges}
            couponDiscountAmounts={couponDiscountAmounts}
          />

          {/* Category Text */}
          <CategoryText groupId={groupId} />
        </div>
      </div>
    </div>
  );
}

export default App;
